import React, { useState, useRef, useEffect } from "react";
import Swal from "sweetalert2";
import Pagination from "../Pagination";
import { Icon } from "@iconify/react";
import "./../../../assets/css/purchase/datatable.css";
import { getReport } from "../../../utils/ReportUtils";
import { useLocation, useNavigate } from "react-router-dom";

export default function ContractTable({
  contracts = [],
  onEdit,
  onView,
  onDelete,
  onSign,
  onCreate,
  onAssignJob,
  fillAvailable = false,
  allowCreate = true,
  search: searchProp,
  onSearchChange,
  currentPage: currentPageProp,
  itemsPerPage: itemsPerPageProp,
  onPageChange,
  onItemsPerPageChange,
  focusedContractId = null,
  onFocusContract,
}) {
  const [currentPageLocal, setCurrentPageLocal] = useState(1);
  const [itemsPerPageLocal, setItemsPerPageLocal] = useState(100);
  const [searchLocal, setSearchLocal] = useState("");

  const isApiSearch = typeof onSearchChange === "function";
  const isControlledPagination =
    typeof currentPageProp === "number" &&
    typeof itemsPerPageProp === "number" &&
    typeof onPageChange === "function" &&
    typeof onItemsPerPageChange === "function";
  const currentPage = isControlledPagination ? currentPageProp : currentPageLocal;
  const itemsPerPage = isControlledPagination ? itemsPerPageProp : itemsPerPageLocal;
  const [inputValue, setInputValue] = useState(isApiSearch ? searchProp ?? "" : "");
  const search = isApiSearch ? searchProp ?? "" : searchLocal;

  useEffect(() => {
    if (isApiSearch) setInputValue(searchProp ?? "");
  }, [searchProp, isApiSearch]);

  const triggerSearch = (v) => {
    if (isApiSearch) {
      onSearchChange(v);
      if (isControlledPagination) onPageChange(1);
      else setCurrentPageLocal(1);
    } else {
      setSearchLocal(v);
      if (isControlledPagination) onPageChange(1);
      else setCurrentPageLocal(1);
    }
  };
  const userRole = localStorage.getItem("role");
  const location = useLocation();
  const navigate = useNavigate();

  const actionRef = useRef(null);
  const statusRef = useRef(null);
  const totalRef = useRef(null);
  const serviceRef = useRef(null);
  const tableScrollRef = useRef(null);

  const [rightStatus, setRightStatus] = useState(0);
  const [rightTotal, setRightTotal] = useState(0);
  const [rightService, setRightService] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const s = params.get("search");
    if (s) {
      if (isApiSearch) {
        onSearchChange?.(s);
        setInputValue(s);
      } else {
        setSearchLocal(s);
      }
      if (isControlledPagination) onPageChange(1);
      else setCurrentPageLocal(1);
    }
  }, [location.search, isApiSearch, isControlledPagination, onPageChange]);

  const handleDelete = (contract) => onDelete?.(contract);

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString("th-TH", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : "-";

  const formatNumber = (num) =>
    Number(num || 0).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const filtered = isApiSearch
    ? contracts
    : contracts.filter((c) => {
        const keyword = search.toLowerCase();
        return (
          (c.Contract_No || "").toLowerCase().includes(keyword) ||
          (c.Quote_No || "").toLowerCase().includes(keyword) ||
          (c.AR_Name || "").toLowerCase().includes(keyword) ||
          (c.AR_LastName || "").toLowerCase().includes(keyword) ||
          (c.DisplayName || "").toLowerCase().includes(keyword) ||
          (c.ServiceAddr_Name || "").toLowerCase().includes(keyword) ||
          (c.Service_Name || "").toLowerCase().includes(keyword) ||
          (String(c.Total_Amount) || "").toLowerCase().includes(keyword) ||
          (String(c.Status) || "").toLowerCase().includes(keyword)
        );
      });

  const totalItems = filtered.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filtered.slice(startIndex, startIndex + itemsPerPage);

  const renderStatus = (status) => {
    const base = "px-2 py-1 rounded-md text-xs font-medium";
    switch (status) {
      case 0:
        return <span className={`${base} bg-gray-100 text-gray-600`}>ร่าง</span>;
      case 1:
        return <span className={`${base} bg-yellow-100 text-yellow-700`}>รอเซ็น</span>;
      case 2:
        return <span className={`${base} bg-green-100 text-green-700`}>ใช้งาน</span>;
      case 5:
        return <span className={`${base} bg-red-100 text-red-600`}>ยกเลิก</span>;
      case 4:
        return <span className={`${base} bg-blue-100 text-blue-700`}>เซ็นแล้ว</span>;
      default:
        return <span className={`${base} bg-gray-200 text-gray-600`}>ไม่ทราบ</span>;
    }
  };

  useEffect(() => {
    let widthAction = 0;
    let widthStatus = 0;
    let widthTotal = 0;
    let widthService = 0;

    if (actionRef.current)
      widthAction = actionRef.current.getBoundingClientRect().width;
    if (statusRef.current)
      widthStatus = statusRef.current.getBoundingClientRect().width;
    if (totalRef.current)
      widthTotal = totalRef.current.getBoundingClientRect().width;
    if (serviceRef.current)
      widthService = serviceRef.current.getBoundingClientRect().width;

    setRightStatus(widthAction);
    setRightTotal(widthAction + widthStatus);
    setRightService(widthAction + widthStatus + widthTotal);
  }, []);

  useEffect(() => {
    if (focusedContractId == null) return;
    const wrapper = tableScrollRef.current;
    if (!wrapper) return;
    const activeRow = wrapper.querySelector("tr.row-focused");
    if (!activeRow) return;
    activeRow.scrollIntoView({ block: "center", behavior: "auto" });
  }, [focusedContractId, currentPage, itemsPerPage, paginatedData.length]);

  return (
    <div
      className="card table-card border-0 rounded-0 shadow-sm"
      style={{
        display: "flex",
        flexDirection: "column",
        height: fillAvailable ? "100%" : "auto",
        maxHeight: fillAvailable ? "100%" : "calc(91vh - 320px)",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <style>{`
        * { font-size: 14px !important; }

        .table-wrapper-scroll { 
          padding: 0 0 0 20px !important; 
        }

        table.table {
          border-collapse: collapse;
          width: 100%;
          min-width: 1500px;
        }

        th, td {
          text-align: center !important;
          vertical-align: middle !important;
          border: 1px solid #ccc !important;
          padding: 8px 12px !important;
          white-space: nowrap;
        }

        thead th {
          background-color: #E13F57 !important;
          color: white !important;
          font-weight: 600;
          position: sticky !important;
          top: 0 !important;
          z-index: 1100 !important;
        }

        th.sticky-action, td.sticky-action {
          position: sticky !important;
          right: 0px !important;
          background: white !important;
          z-index: 20 !important;
          isolation: isolate;
          will-change: transform;
          transform: translateZ(0);
        }

        th.sticky-status, td.sticky-status {
          position: sticky !important;
          right: ${rightStatus}px !important;
          background: white !important;
          z-index: 99 !important;
          isolation: isolate;
          will-change: transform;
          transform: translateZ(0);
        }

        th.sticky-total, td.sticky-total {
          position: sticky !important;
          right: ${rightTotal}px !important;
          background: white !important;
          z-index: 98 !important;
          isolation: isolate;
          will-change: transform;
          transform: translateZ(0);
        }

        th.sticky-service, td.sticky-service {
          position: sticky !important;
          right: ${rightService}px !important;
          background: white !important;
          z-index: 97 !important;
          isolation: isolate;
          will-change: transform;
          transform: translateZ(0);
        }

        /* Ensure hover doesn't affect sticky columns */
        tbody tr:hover td.sticky-action,
        tbody tr:hover td.sticky-status,
        tbody tr:hover td.sticky-total,
        tbody tr:hover td.sticky-service {
          background: white !important;
        }

        tbody tr.row-focused td {
          background: #fdf2f8 !important;
        }

        tbody tr.row-focused:hover td {
          background: #fdf2f8 !important;
        }

        thead th.sticky-action {
          background-color: #E13F57 !important;
          top: 0 !important;
          z-index: 1103 !important;
          position: sticky !important;
          right: 0px !important;
        }

        thead th.sticky-status {
          background-color: #E13F57 !important;
          top: 0 !important;
          z-index: 1102 !important;
          position: sticky !important;
          right: ${rightStatus}px !important;
        }

        thead th.sticky-total {
          background-color: #E13F57 !important;
          top: 0 !important;
          z-index: 1102 !important;
          position: sticky !important;
          right: ${rightTotal}px !important;
        }

        thead th.sticky-service {
          background-color: #E13F57 !important;
          top: 0 !important;
          z-index: 1102 !important;
          position: sticky !important;
          right: ${rightService}px !important;
        }

        th.sticky-action, td.sticky-action,
        th.sticky-status, td.sticky-status,
        th.sticky-total, td.sticky-total,
        th.sticky-service, td.sticky-service {
          box-shadow: -2px 0 3px rgba(0,0,0,0.05);
        }
      `}</style>

      <div className="d-flex justify-content-between align-items-center px-4 py-2 border-bottom">

        <div className="d-flex align-items-center gap-3">
          <h5 className="m-0 fw-bold">ข้อมูลสัญญา</h5>

          <div className="relative">
            <input
              type="text"
              placeholder={isApiSearch ? "ค้นหา... (กด Enter)" : "ค้นหา..."}
              value={isApiSearch ? inputValue : search}
              onChange={(e) => {
                if (isApiSearch) {
                  setInputValue(e.target.value);
                } else {
                  triggerSearch(e.target.value);
                }
              }}
              onKeyDown={(e) => {
                if (isApiSearch && e.key === "Enter") {
                  triggerSearch(inputValue);
                }
              }}
              className="h-[36px] w-[250px] border border-gray-300 rounded-md px-3 pr-8 py-1 text-sm"
            />

            {(isApiSearch ? inputValue : search) !== "" && (
              <button
                onClick={() => {
                  setInputValue("");
                  triggerSearch("");
                  if (isControlledPagination) onPageChange(1);
                  else setCurrentPageLocal(1);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <Icon icon="mdi:close" className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {allowCreate && (
          <button
            onClick={onCreate}
            className="flex items-center gap-1 text-[#F17F25] text-sm font-semibold border-b-2 border-[#F17F25]"
          >
            <Icon icon="mdi:plus-circle-outline" className="w-5 h-5" />
            เพิ่มสัญญา
          </button>
        )}
      </div>

      <div
        ref={tableScrollRef}
        className="table-wrapper-scroll flex-1"
        style={{ overflowY: "auto", overflowX: "auto", flexGrow: 1 }}
      >
        <table className="table table-bordered table-hover mb-0">
          <thead>
            <tr>
              <th>ลำดับ</th>
              <th>เลขที่สัญญา</th>
              <th>วันที่สัญญา</th>
              <th>ใบเสนอราคา</th>
              <th>ชื่อลูกค้าในสัญญา</th>
              <th>ชื่อลูกค้า</th>
              <th>ชื่อสถานที่บริการ</th>

              <th ref={serviceRef} className="sticky-service">ชื่อบริการ</th>

              <th ref={totalRef} className="sticky-total">ยอดรวม (บาท)</th>

              <th ref={statusRef} className="sticky-status">สถานะ</th>

              <th ref={actionRef} className="sticky-action">การจัดการ</th>
            </tr>
          </thead>

          <tbody>
            {paginatedData.length ? (
              paginatedData.map((c, i) => (
                <tr
                  key={c.Contract_Id || i}
                  onClick={() => onFocusContract?.(c)}
                  className={
                    focusedContractId != null &&
                    String(c.Contract_Id) === String(focusedContractId)
                      ? "row-focused"
                      : ""
                  }
                >
                  <td>{startIndex + i + 1}</td>
                  <td>{c.Contract_No}</td>
                  <td>{formatDate(c.Contract_Date)}</td>
                  <td>{c.Quote_No}</td>
                  <td className="text-start">
                    {c.AR_Id ? (
                      <button
                        type="button"
                        className="btn btn-link p-0 text-primary text-decoration-none text-start border-0 hover:text-blue-700"
                        onClick={() => navigate(`/EditCustomer/${c.AR_Id}`)}
                        style={{ cursor: "pointer" }}
                      >
                        {c.DisplayName || "-"}
                      </button>
                    ) : (
                      c.DisplayName || "-"
                    )}
                  </td>
                  <td className="text-start">{c.AR_Name} {c.AR_LastName}</td>
                  <td className="text-start">{c.ServiceAddr_Name}</td>

                  <td className="text-start sticky-service">{c.Service_Name || "-"}</td>

                  <td className="text-end sticky-total">{formatNumber(c.Total_Amount)}</td>

                  <td className="sticky-status">{renderStatus(c.Status)}</td>

                  <td className="sticky-action">
                    <div
                      className="d-grid"
                      style={{
                        gridTemplateColumns: c.Status === 4 
                          ? (userRole === "Admin" ? "110px 90px 65px 40px" : "110px 40px")
                          : (userRole === "Admin" ? "110px 90px 65px 40px" : "110px 40px"),
                        gap: "6px",
                      }}
                    >
                      {c.Status === 5 ? (
                        <>
                          <button
                            onClick={() => onEdit?.(c)}
                            className="btn btn-sm text-white"
                            style={{ backgroundColor: "#598AE4" }}
                          >
                            ดูข้อมูล
                          </button>
                        </>
                      ) : c.Status === 4 ? (
                        <>
                          <button
                            onClick={() => onView?.(c)}
                            className="btn btn-sm text-white"
                            style={{ backgroundColor: "#598AE4" }}
                          >
                            ดูข้อมูล
                          </button>

                          {userRole === "Admin" && (
                            <button
                              onClick={() => onAssignJob?.(c)}
                              className="btn btn-sm text-white"
                              style={{ backgroundColor: "#F17F25" }}
                            >
                              Assign Job
                            </button>
                          )}

                          {userRole === "Admin" && (
                            <button
                              onClick={() => handleDelete(c)}
                              className="btn btn-sm text-white"
                              style={{ backgroundColor: "#E13F57" }}
                            >
                              ลบ
                            </button>
                          )}

                          <button
                            type="button"
                            onClick={() => getReport("contract", c.Contract_Id)}
                            className="btn btn-sm flex items-center justify-center transition-opacity hover:opacity-70"
                            style={{ minWidth: "40px", height: "32px", background: "transparent", border: "none" }}
                          >
                            <Icon icon="mdi:printer" width="18" color="#007bff" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => onSign?.(c)}
                            className="btn btn-sm text-white"
                            style={{ backgroundColor: "#16a34a" }}
                          >
                            เซ็น
                          </button>

                          {userRole === "Admin" && (
                            <button
                              onClick={() => onEdit?.(c)}
                              className="btn btn-sm text-white"
                              style={{ backgroundColor: "#598AE4" }}
                            >
                              แก้ไข
                            </button>
                          )}

                          {userRole === "Admin" && (
                            <button
                              onClick={() => handleDelete(c)}
                              className="btn btn-sm text-white"
                              style={{ backgroundColor: "#E13F57" }}
                            >
                              ลบ
                            </button>
                          )}

                          <button
                            type="button"
                            onClick={() => getReport("contract", c.Contract_Id)}
                            className="btn btn-sm flex items-center justify-center transition-opacity hover:opacity-70"
                            style={{ minWidth: "40px", height: "32px", background: "transparent", border: "none" }}
                          >
                            <Icon icon="mdi:printer" width="18" color="#007bff" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>

                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={11} className="text-center py-4 text-muted">
                  ไม่มีข้อมูลสัญญา
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        onPageChange={(page) => {
          if (isControlledPagination) onPageChange(page);
          else setCurrentPageLocal(page);
        }}
        onItemsPerPageChange={(val) => {
          if (isControlledPagination) {
            onItemsPerPageChange(val);
            onPageChange(1);
          } else {
            setItemsPerPageLocal(val);
            setCurrentPageLocal(1);
          }
        }}
      />
    </div>
  );
}
